import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { DateRange, DefaultMatCalendarRangeStrategy, MatCalendar, MAT_DATE_RANGE_SELECTION_STRATEGY } from '@angular/material/datepicker';
import { DialogPosition, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DateTime, Interval } from 'luxon';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

@Component({
    selector: 'app-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy },
        { provide: DateAdapter, useClass: NativeDateAdapter }
    ]
})
export class DateRangePickerComponent implements OnInit, OnChanges {
    private static readonly pagePeriod = 30;
    private static readonly minJsStartDate = DateTime.now().minus({ years: 100 }).startOf('day').toJSDate(); // 100 years ago
    private static readonly maxJsEndDate = DateTime.now().endOf('day').toJSDate(); // present date

    @Input() initialRangeValue: Interval = Interval.fromDateTimes(
        DateTime.now().startOf('day').toJSDate(),
        DateRangePickerComponent.maxJsEndDate
    );

    @Output() selectedRangeValueChange = new EventEmitter<Interval>();

    @ViewChild('dateRangeInput') dateRangeInput: ElementRef;
    @ViewChild('calendar', { static: false }) calendar: MatCalendar<Date>;

    startDate: Date;
    endDate: Date;
    minDate = DateRangePickerComponent.minJsStartDate;
    maxDate = DateRangePickerComponent.maxJsEndDate;
    selectedRangeValueSubject = new BehaviorSubject<DateRange<Date>>(new DateRange<Date>(null, null));
    dialogRef: MatDialogRef<any>;

    isPreviousBtnDisabled$: Observable<boolean>;
    isNextBtnDisabled$: Observable<boolean>;

    constructor(public dialog: MatDialog) {}

    ngOnInit(): void {
        this.startDate = this.initialRangeValue.start.toJSDate();
        this.endDate = this.initialRangeValue.end.toJSDate();
        this.selectedRangeValueSubject.next(new DateRange<Date>(this.startDate, this.endDate));
        this.initMonthBtnsAvailability();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.initialRangeValue?.currentValue) {
            const initialRangeValueChanges = changes.initialRangeValue.currentValue as Interval;
            const rangeValue = this.selectedRangeValueSubject.value;
            if (!initialRangeValueChanges.start.equals(DateTime.fromJSDate(rangeValue.start))) {
                this.startDate = initialRangeValueChanges.start.toJSDate();
                this.endDate = initialRangeValueChanges.end.toJSDate();
                this.selectedRangeValueSubject.next(new DateRange<Date>(this.startDate, this.endDate));
            }
        }
    }

    /**
     * Opens the date range picker dialog
     */
    openDatePickerDialog = (dialogRef: TemplateRef<any>): void => {
        // Reset min and max dates everytime before opening the dialog to cater for date change after midnight
        this.setMinMaxDate();

        const dialogConfig: MatDialogConfig = {
            position: this.getDialogPosition(),
            panelClass: 'date-range-picker-dialog'
        };

        this.dialogRef = this.dialog.open(dialogRef, dialogConfig);
        this.dialogRef.afterClosed().subscribe(() => {
            // if the dialog was closed without selecting start as well as end date then
            // reset selection for the calendar control
            if (!this.selectedRangeValueSubject.value?.end) {
                this.selectedRangeValueSubject.next(new DateRange<Date>(this.startDate, this.endDate));
            }
        });
    };

    /**
     * Returns position for the date range picker dialog according to the position of
     * the corresponding input control
     */
    getDialogPosition = (): DialogPosition => {
        try {
            const inputPosition = this.dateRangeInput.nativeElement.getBoundingClientRect() as DOMRect;
            return {
                top: `${inputPosition.bottom}px`,
                left: `${inputPosition.left}px`
            };
        } catch {
            // Ignore error if we cannot determine the position of the input control.
            // The dialog will open in the centre of the screen
            return undefined;
        }
    };

    /**
     * Sets range for calendar and input control using specified days.
     * Called from the presets on the date range picker dialog.
     * @param days number of days
     */
    setRange = (days: number): void => {
        const newStartDate = DateTime.now().minus({ days }).startOf('day');
        const newEndDate = DateTime.fromJSDate(DateRangePickerComponent.maxJsEndDate);
        this.updateSelectionAndEmit(newStartDate, newEndDate);
        this.dialogRef?.close();
    };

    /**
     * Go to previous 30 days (the number is approximate, it could be 28, 29, 30 or 31 days)
     * It reads the start date of the existing range, subtract 1 month, as the new start date.
     * The new end date is one day prior to the existing start date.
     *
     * For more examples, check the unit tests in 'DateRangePickerComponent' > 'Previous and Next buttons' > 'Functionality'
     *
     * Special case:
     * If existing start date is 30th (or 29th or 31th) Mar, the new start date is 28th Feb.
     */
    previousMonth = (): void => {
        const rangeValue = this.selectedRangeValueSubject.value;
        const originalStartDate = DateTime.fromJSDate(rangeValue.start);
        const newStartDate = originalStartDate.minus({ month: 1 }).startOf('day');
        const newEndDate = originalStartDate.minus({ day: 1 }).endOf('day');

        // make the calendar follow the selected date and turn pages
        this.calendar.activeDate = newStartDate.toJSDate();
        this.setMinMaxDate();

        this.updateSelectionAndEmit(newStartDate, newEndDate);
    };

    /**
     * Go to next 30 days (the number is approximate, it could be 28, 29, 30 or 31 days)
     * It reads the end date of the existing range, plus 1 day, as the new start date.
     * The new end date is the existing end date plus 1 month.
     *
     * For more examples, check the unit tests in 'DateRangePickerComponent' > 'Previous and Next buttons' > 'Functionality'
     *
     * Special case:
     * If existing end date is the last day of the month, the new end date should also be the last day of the next month.
     */
    nextMonth = (): void => {
        const rangeValue = this.selectedRangeValueSubject.value;
        const originalEndDate = DateTime.fromJSDate(rangeValue.end ?? rangeValue.start);
        const isLastDayOfMonth = originalEndDate.day === originalEndDate.daysInMonth;
        const newStartDate = originalEndDate.plus({ day: 1 }).startOf('day');
        let newEndDate;
        if (isLastDayOfMonth) {
            const nextMonth = originalEndDate.plus({ month: 1 });
            newEndDate = nextMonth.set({ day: nextMonth.daysInMonth }).endOf('day');
        } else {
            newEndDate = originalEndDate.plus({ month: 1 }).endOf('day');
        }
        // make the calendar follow the selected date and turn pages
        this.calendar.activeDate = newStartDate.toJSDate();
        this.setMinMaxDate();

        this.updateSelectionAndEmit(newStartDate, newEndDate);
    };

    private updateSelectionAndEmit(newStartDate: DateTime, newEndDate: DateTime) {
        this.startDate = newStartDate.toJSDate();
        this.endDate = newEndDate.toJSDate();
        this.selectedRangeValueSubject.next(new DateRange<Date>(newStartDate.toJSDate(), newEndDate.toJSDate()));
        this.selectedRangeValueChange.emit(Interval.fromDateTimes(newStartDate, newEndDate));
    }

    /**
     * Disable the buttons when the selected range is close to the range limit
     */
    initMonthBtnsAvailability = (): void => {
        const validRangeValue$ = this.selectedRangeValueSubject.pipe(
            filter(val => !!val.start && !!val.end),
            shareReplay()
        );
        this.isPreviousBtnDisabled$ = validRangeValue$.pipe(
            map(
                val =>
                    DateTime.fromJSDate(val.start).minus({ days: DateRangePickerComponent.pagePeriod }).startOf('day') <
                    DateTime.fromJSDate(DateRangePickerComponent.minJsStartDate)
            )
        );
        this.isNextBtnDisabled$ = validRangeValue$.pipe(
            map(
                val =>
                    DateTime.fromJSDate(val.end).plus({ days: DateRangePickerComponent.pagePeriod }).endOf('day') >
                    DateTime.fromJSDate(DateRangePickerComponent.maxJsEndDate)
            )
        );
    };

    /**
     * Called when the date selection is changed using the calendar.
     * If both start and end dates are selected then the control emits the selectedRangeValueChange event.
     * @param date selected date
     */
    selectedChange = (date: Date): void => {
        const rangeValue = this.selectedRangeValueSubject.value;
        if (rangeValue.start && rangeValue.end) {
            // Either a new start or end date is selected
            this.selectedRangeValueSubject.next(new DateRange<Date>(DateTime.fromJSDate(date).startOf('day').toJSDate(), null));
            this.setMinMaxDate(date);
        } else if (rangeValue.start) {
            // A new range (start and end) is selected
            let newStartDate: DateTime;
            let newEndDate: DateTime;
            if (date >= rangeValue.start) {
                newStartDate = DateTime.fromJSDate(rangeValue.start);
                newEndDate = DateTime.fromJSDate(date).endOf('day');
            } else {
                newStartDate = DateTime.fromJSDate(date).startOf('day');
                newEndDate = DateTime.fromJSDate(rangeValue.start).endOf('day');
            }
            this.updateSelectionAndEmit(newStartDate, newEndDate);
            this.dialogRef?.close();
        }
    };

    private setMinMaxDate(selectedJsDate?: Date) {
        if (!selectedJsDate) {
            // reset max and min dates
            this.maxDate = this.setMaxDate();
            this.minDate = this.setMinDate();
        } else {
            this.maxDate = this.setMaxDate(selectedJsDate);
            this.minDate = this.setMinDate(selectedJsDate);
        }
    }

    /**
     * This method returns the allowed end JS date, either by 30 days after the given selected date,
     * or by using the maxJsEndDate.
     * @param selectedJsDate The selected date, for calculating the allowed end date.
     * @returns The allowed end JS date
     */
    private setMaxDate(selectedJsDate?: Date) {
        if (!selectedJsDate) {
            return DateRangePickerComponent.maxJsEndDate;
        } else {
            const newEndRange = DateTime.fromJSDate(selectedJsDate).plus({ days: 30 }).endOf('day');
            const maxDateRange = DateTime.fromJSDate(DateRangePickerComponent.maxJsEndDate);
            const resultEndLimit = newEndRange < maxDateRange ? newEndRange : maxDateRange;
            return resultEndLimit.toJSDate();
        }
    }

    /**
     * This method is a similar method with setMaxDate().
     * Because user can select a previous date after selecting the first date of the range in calendar,
     * it prevents user from selecting a date earlier than 30 days of the first date.
     * It returns the allowed start JS date, either by 30 days before the given selected date,
     * or by using the minJsStartDate.
     * @param selectedJsDate The selected date, for calculating the allowed start date.
     * @returns The allowed start JS date
     */
    private setMinDate(selectedJsDate?: Date) {
        if (!selectedJsDate) {
            return DateRangePickerComponent.minJsStartDate;
        } else {
            const newStartRange = DateTime.fromJSDate(selectedJsDate).minus({ days: 30 }).startOf('day');
            const minDateRange = DateTime.fromJSDate(DateRangePickerComponent.minJsStartDate);
            const resultStartLimit = newStartRange > minDateRange ? newStartRange : minDateRange;
            return resultStartLimit.toJSDate();
        }
    }

    formatDates(dateRange: DateRange<Date>): string {
        const originalStartDate = DateTime.fromJSDate(dateRange.start);
        const originalEndDate = DateTime.fromJSDate(dateRange.end);

        const now = DateTime.now();
        let startDateText = '';
        let endDateText = '';
        if (originalStartDate.isValid && now.year !== originalStartDate.year) {
            startDateText = originalStartDate.isValid ? originalStartDate.toFormat('dd MMM yyyy') : '';
            endDateText = originalEndDate.isValid ? originalEndDate.toFormat('dd MMM yyyy') : '';
        } else {
            startDateText = originalStartDate.isValid ? originalStartDate.toFormat('dd MMM') : '';
            endDateText = originalEndDate.isValid ? originalEndDate.toFormat('dd MMM') : '';
        }
        return `${startDateText} - ${endDateText}`;
    }
}
